import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { buildThumbnailBlocks, ThumbnailSection } from './template-thumbnail.model';
import { TemplatePreviewInput, TemplatePreviewService } from './template-preview.service';

/** Fases da prévia que encerram a paginação (ver o bootstrap do pdf-preview-composer). */
const FINAL_PHASES = ['rendered', 'partial', 'fallback'];

/**
 * Prévia de um modelo no estilo do Word: uma página A4 em miniatura, cortada embaixo.
 *
 * Com `preview`, a folha é a PRIMEIRA PÁGINA DE VERDADE, gerada pelo mesmo renderizador
 * da prévia do PDF (ver TemplatePreviewService). Enquanto ela não fica pronta, e quando
 * falha, aparece o desenho da estrutura do modelo (títulos, parágrafos, tabelas...), que
 * serve de esqueleto com a forma certa. A cor de destaque é a do tipo do documento.
 *
 * A prévia só é pedida quando o card aparece na tela.
 */
@Component({
    selector: 'template-thumbnail',
    templateUrl: 'template-thumbnail.component.html',
    styleUrl: 'template-thumbnail.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        'aria-hidden': 'true',
        '[style.--thumb-accent]': 'accent()',
        '[style.--thumb-accent-light]': 'accentLight()',
    },
})
export class TemplateThumbnailComponent {
    public sections = input<ThumbnailSection[] | undefined>([]);
    public quote = input(false);
    /** Página em branco (opção "Em branco"): sem cabeçalho nem conteúdo. */
    public blank = input(false);
    public accent = input('var(--primary)');
    public accentLight = input('var(--primaryLight)');
    /** Dados para a página real. Sem eles fica só o desenho da estrutura. */
    public preview = input<TemplatePreviewInput | null>(null);

    private _previews = inject(TemplatePreviewService);
    private _sanitizer = inject(DomSanitizer);
    private _host = inject<ElementRef<HTMLElement>>(ElementRef);

    private _frame = viewChild<ElementRef<HTMLIFrameElement>>('frame');
    private _visible = signal(false);

    public srcdoc = signal<SafeHtml | null>(null);
    /** A folha real terminou de paginar: o desenho sai de baixo dela. */
    public ready = signal(false);

    public blocks = computed(() =>
        this.blank() ? [] : buildThumbnailBlocks(this.sections(), { quote: this.quote() })
    );

    constructor() {
        const destroyRef = inject(DestroyRef);

        const observer = new IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    this._visible.set(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '200px' }
        );
        observer.observe(this._host.nativeElement);

        const onMessage = (event: MessageEvent): void => {
            const frame = this._frame()?.nativeElement;
            if (!frame || event.source !== frame.contentWindow) return;
            if (event.data?.source !== 'rapidocs-pdf-preview') return;
            if (FINAL_PHASES.includes(event.data.phase) && event.data.pages > 0) this.ready.set(true);
        };
        window.addEventListener('message', onMessage);

        destroyRef.onDestroy(() => {
            observer.disconnect();
            window.removeEventListener('message', onMessage);
        });

        effect(() => {
            const preview = this.preview();
            if (!preview || this.blank() || !this._visible()) return;

            this._previews.srcdocFor(preview).then(srcdoc => {
                // O card pode ter trocado de modelo enquanto a prévia era gerada.
                if (!srcdoc || this.preview()?.key !== preview.key) return;
                this.srcdoc.set(this._sanitizer.bypassSecurityTrustHtml(srcdoc));
            });
        });
    }

    /** Linhas e colunas viram listas para o `@for` do template. */
    public range(count: number): number[] {
        return Array.from({ length: count }, (_, i) => i);
    }
}
